import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { requestService, allocationService } from '../services/supabaseClient';
import '../styles/MyStudents.css';

// ============================================================
// DB CONNECTED
// ------------------------------------------------------------
// Each row here is one `requests` row (= one registration) for the
// current partner, already joined with the student and course
// details (requestService.getForPartner). Because `requests` is
// per-course, the SAME student can appear more than once in this
// list — once per course they're registered against, including
// twice for the same course if they were registered again.
//
// requests.status has a DB check constraint allowing ONLY:
//   'registered' | 'pending' | 'completed' | 'cancelled'
// ('certification_requested' is NOT one of them and was violating
// requests_status_check.) So the certification-request flow reuses
// 'pending' to mean "certificate requested, awaiting admin action":
//   registered -> pending (🎓 here) -> completed / cancelled (admin side)
//
// "Request Certification" (🎓) doesn't open a separate form — it
// flips this request row's status to 'pending' and sends the partner
// to the Requests page, where the admin-side approval/issuance flow
// (separate `certificates` table) picks it up.
//
// Deleting a row removes that ONE registration (`requests` row) and
// frees up the seat on the allocation — it does NOT delete the
// student record itself, since the same person may have other
// registrations.
// ============================================================

const CERT_REQUESTED_STATUS = 'pending';

export default function PartnerStudent({ session }) {
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [activeRow, setActiveRow] = useState(null); // for view modal
  const [busyId, setBusyId] = useState(null); // enrollment_id currently being deleted/requested

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await requestService.getForPartner(session.partnerId);
      setRows(data || []);
    } catch (err) {
      setError(err.message || 'Unable to load your students.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (session?.partnerId) load();
  }, [session?.partnerId]);

  const filteredRows = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return rows;

    return rows.filter(row => {
      const s = row.students;
      const studentId = (s?.student_id || '').toLowerCase();
      const name = `${s?.first_name || ''} ${s?.last_name || ''}`.toLowerCase();
      const email = (s?.email || '').toLowerCase();

      return studentId.includes(query) || name.includes(query) || email.includes(query);
    });
  }, [rows, search]);

  const formatDate = value => {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const studentName = row => `${row.students?.first_name || ''} ${row.students?.last_name || ''}`.trim();
  const courseLabel = row =>
    row.course
      ? `${row.course.name}${row.course.course_code ? ` (${row.course.course_code})` : ''}`
      : '—';

  // Maps a request's `status` column to what the badge should show.
  // Kept in one place so the table and the modal never drift apart.
  const statusBadge = status => {
    switch (status) {
      case CERT_REQUESTED_STATUS:
        return { className: 'badge-requested', label: 'Certification Requested' };
      case 'completed':
        return { className: 'badge-completed', label: 'Completed' };
      case 'cancelled':
        return { className: 'badge-cancelled', label: 'Cancelled' };
      default:
        return { className: 'badge-registered', label: 'Registered' };
    }
  };

  // Removes this ONE registration and frees the seat back on the
  // allocation. Confirmed first since it can't be undone.
  const handleDeleteRow = async row => {
    const ok = window.confirm(`Remove ${studentName(row)}'s registration for "${row.course?.name || 'this course'}"? This can't be undone.`);
    if (!ok) return;

    setBusyId(row.enrollment_id);
    setError('');
    try {
      await requestService.remove(row.enrollment_id);
      if (row.allocation_id) {
        await allocationService.adjustSeatsUsed(row.allocation_id, -1);
      }
      setRows(prev => prev.filter(r => r.enrollment_id !== row.enrollment_id));
      if (activeRow?.enrollment_id === row.enrollment_id) setActiveRow(null);
    } catch (err) {
      setError(err.message || 'Could not remove this registration.');
    } finally {
      setBusyId(null);
    }
  };

  // Flips this request's status so it shows up in the certification
  // pipeline, then sends the partner to the Requests page to track it.
  const goToRequestCertification = async row => {
    setBusyId(row.enrollment_id);
    setError('');
    try {
      if (row.status !== CERT_REQUESTED_STATUS) {
        await requestService.updateStatus(row.enrollment_id, CERT_REQUESTED_STATUS);
        setRows(prev =>
          prev.map(r => (r.enrollment_id === row.enrollment_id ? { ...r, status: CERT_REQUESTED_STATUS } : r))
        );
      }
      navigate('/academy/partner/requests');
    } catch (err) {
      setError(err.message || 'Could not request certification.');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="panel">
      <div className="panel-head students-head">
        <div>
          <h2>My Students</h2>
          <p>Students you have registered against your allocated content.</p>
        </div>
      </div>

      <div className="students-toolbar">
        <div className="search-box">
          <span className="search-icon">🔍</span>
          <input
            type="text"
            placeholder="Search student..."
            value={search}
            onChange={event => setSearch(event.target.value)}
          />
        </div>
      </div>

      {error && <div className="error">{error}</div>}

      <div className="table-wrapper students-table-wrapper">
        <table className="students-table">
          <thead>
            <tr>
              <th>Student ID</th>
              <th>Name</th>
              <th>Email</th>
              <th>Course</th>
              <th>Registered On</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>

          <tbody>
            {loading && (
              <tr>
                <td colSpan={7} className="empty-state">
                  Loading students...
                </td>
              </tr>
            )}

            {!loading && filteredRows.length === 0 && (
              <tr>
                <td colSpan={7} className="empty-state">
                  {search ? 'No students match your search.' : 'No students registered yet.'}
                </td>
              </tr>
            )}

            {!loading &&
              filteredRows.map(row => {
                const badge = statusBadge(row.status);
                return (
                  <tr key={row.enrollment_id}>
                    <td className="student-id-cell">{row.students?.student_id}</td>
                    <td>{studentName(row)}</td>
                    <td>{row.students?.email}</td>
                    <td>{courseLabel(row)}</td>
                    <td>{formatDate(row.created_at)}</td>
                    <td>
                      <span className={`badge ${badge.className}`}>{badge.label}</span>
                    </td>
                    <td className="action-cell">
                      <button
                        type="button"
                        className="icon-btn"
                        title="View details"
                        onClick={() => setActiveRow(row)}
                      >
                        👁
                      </button>
                      <button
                        type="button"
                        className="icon-btn"
                        title="Request certification"
                        disabled={busyId === row.enrollment_id || row.status === CERT_REQUESTED_STATUS || row.status === 'completed'}
                        onClick={() => goToRequestCertification(row)}
                      >
                        🎓
                      </button>
                      <button
                        type="button"
                        className="icon-btn icon-btn-danger"
                        title="Delete registration"
                        disabled={busyId === row.enrollment_id}
                        onClick={() => handleDeleteRow(row)}
                      >
                        🗑
                      </button>
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>

      {activeRow && (
        <div className="modal-backdrop" onClick={() => setActiveRow(null)}>
          <div className="modal-card" onClick={event => event.stopPropagation()}>
            <div className="modal-head">
              <h3>Student Details</h3>
              <button type="button" className="modal-close" onClick={() => setActiveRow(null)}>
                ✕
              </button>
            </div>

            <div className="modal-body">
              <div className="modal-row">
                <span>Student ID</span>
                <b>{activeRow.students?.student_id}</b>
              </div>
              <div className="modal-row">
                <span>Name</span>
                <b>{studentName(activeRow)}</b>
              </div>
              <div className="modal-row">
                <span>Email</span>
                <b>{activeRow.students?.email}</b>
              </div>
              <div className="modal-row">
                <span>Mobile</span>
                <b>{activeRow.students?.mobile || '—'}</b>
              </div>
              <div className="modal-row">
                <span>Organisation</span>
                <b>{activeRow.students?.organisation || '—'}</b>
              </div>
              <div className="modal-row">
                <span>Course</span>
                <b>{courseLabel(activeRow)}</b>
              </div>
              <div className="modal-row">
                <span>Registered On</span>
                <b>{formatDate(activeRow.created_at)}</b>
              </div>
              <div className="modal-row">
                <span>Status</span>
                <span className={`badge ${statusBadge(activeRow.status).className}`}>
                  {statusBadge(activeRow.status).label}
                </span>
              </div>
            </div>

            <div className="modal-actions">
              <button
                type="button"
                className="secondary danger"
                onClick={() => handleDeleteRow(activeRow)}
              >
                Delete
              </button>
              <button
                type="button"
                className="primary"
                disabled={activeRow.status === CERT_REQUESTED_STATUS || activeRow.status === 'completed'}
                onClick={() => {
                  goToRequestCertification(activeRow);
                  setActiveRow(null);
                }}
              >
                Request Certification
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}